import {
  TimerGameObject,
  Vector2,
  quatToVector2D,
  vector2DToQuat,
  vector2IsZero,
} from "realms-engine";
import {
  ControllablePlayerGameObject,
  ControllablePlayerGameObjectEventMap,
} from "./controllablePlayer.gameObject";
import {
  CreatureState,
  CreatureStateType,
  PlayerState,
  SERVER_SIDE_AUTHORITATIVE_STATES,
} from "mmo-common";
import { mapCharacterStateToAnimation } from "./mapCharacterStateToAnimation";
import { PlayerFloatingWeapon } from "../floating-weapon/playerFloatingWeapon.gameObject";

const AIRBORNE_TIMER_MS = 500;

/**
 * The character that the player controls
 */
export class SelfPlayerGameObject extends ControllablePlayerGameObject {
  private currentState: CreatureState = { type: CreatureStateType.Idle };
  private airborneTimer: TimerGameObject;
  private floatingWeapon: PlayerFloatingWeapon;
  private bufferedMoveDirection: Vector2 | null = null;
  private normalAttackTimer: TimerGameObject;

  constructor(state: PlayerState) {
    super(state);

    this.addListener("isTouchingGround", this.onIsTouchingGroundEvent);
    this.airborneTimer = this.addChild(new TimerGameObject());
    this.airborneTimer.addListener("onFinish", () => this.onAirborneTimerEnd());
    this.floatingWeapon = this.addChild(
      new PlayerFloatingWeapon({ playerState: state })
    );
    this.floatingWeapon.addListener("onShowChange", (show) => {
      this.characterGameObject.setWeaponVisibility(!show);
      this.playCurrentStateAnimation();
    });

    this.characterGameObject.setWeaponVisibility(
      !this.floatingWeapon.isShowing()
    );
    this.addListener("onMasterCreatureStateChange", (e) =>
      this.onMasterCreatureStateChange(e)
    );
    this.normalAttackTimer = this.addChild(new TimerGameObject());
    this.normalAttackTimer.addListener("onFinish", () =>
      this.onAttackComplete()
    );
  }

  /**
   * When the creature state has changed from the master state
   */
  onMasterCreatureStateChange({
    newState,
  }: ControllablePlayerGameObjectEventMap["onMasterCreatureStateChange"]) {
    if (SERVER_SIDE_AUTHORITATIVE_STATES.includes(newState.type)) {
      this.setCurrentState(newState);
    }
  }

  /**
   * Called when an attack is completed
   */
  onAttackComplete() {
    if (this.bufferedMoveDirection) {
      this.setCurrentState({
        type: CreatureStateType.Walk,
      });
      this.move(this.bufferedMoveDirection);
      return;
    }
    this.setCurrentState({
      type: CreatureStateType.Idle,
    });
  }

  override onStep(delta: number): void {
    super.onStep(delta);
    this.floatingWeapon.updateFloatingWeaponPosition({
      quaternion: this.getQuaternion(),
      position: this.getPosition(),
    });
  }

  getCurrentState() {
    return this.currentState;
  }

  /**
   * Gets the client's player state
   */
  getClientPlayerState(): PlayerState {
    const masterState = this.getMasterPlayerState();
    return {
      ...masterState,
      creature: {
        ...masterState.creature,
        state: this.getCurrentState(),
        position: this.getPosition(),
        quaternion: this.getQuaternion(),
      },
    };
  }

  private setCurrentState(state: CreatureState) {
    const oldState = this.getCurrentState();
    if (oldState.type !== state.type) {
      this.onCurrentStateChange(oldState, state);
    }
    // Update the current state
    this.currentState = state;
    this.playCurrentStateAnimation();
  }

  private playCurrentStateAnimation() {
    const playerState = this.getClientPlayerState();
    // Play the animation of the new state
    this.floatingWeapon.updateFloatingWeaponState(playerState);
    const animation = mapCharacterStateToAnimation(playerState, {
      showingFloatingWeapon: Boolean(this.floatingWeapon.isShowing()),
    });
    this.characterGameObject.playAnimation(animation);
  }

  /**
   * Syncs simulated state with master
   */
  syncWithMaster() {
    const masterState = this.getMasterPlayerState();
    this.setPosition(masterState.creature.position);
    this.setQuaternion(masterState.creature.quaternion);
    this.setCurrentState(masterState.creature.state);
  }

  /**
   * Called when the is touching ground event is fired
   */
  private onIsTouchingGroundEvent = (isTouching: boolean) => {
    // If jumping and touching, instantly change
    if (this.getCurrentState().type === CreatureStateType.Jump) {
      // Jump state ended because it touched the ground
      if (isTouching) {
        this.setCurrentState({
          type: vector2IsZero({
            x: this.movingForce.force.x,
            y: this.movingForce.force.z,
          })
            ? CreatureStateType.Idle
            : CreatureStateType.Walk,
        });
      }

      // If in jumping state no need to handle
      return;
    }

    if (isTouching) {
      this.airborneTimer.stop();
    } else {
      this.airborneTimer.start(AIRBORNE_TIMER_MS);
    }
  };

  /**
   * Called when the airborneTimer ends
   */
  private onAirborneTimerEnd = () => {
    switch (this.getCurrentState().type) {
      case CreatureStateType.Idle: {
        this.setCurrentState({ type: CreatureStateType.Jump });
        break;
      }
      case CreatureStateType.Walk: {
        this.setCurrentState({ type: CreatureStateType.Jump });
        break;
      }
      case CreatureStateType.Jump: {
        this.setCurrentState({
          type: vector2IsZero({
            x: this.movingForce.force.x,
            y: this.movingForce.force.z,
          })
            ? CreatureStateType.Idle
            : CreatureStateType.Walk,
        });
        break;
      }
      case CreatureStateType.Death: {
        break;
      }
      case CreatureStateType.NormalAttack: {
        this.setCurrentState({ type: CreatureStateType.Jump });
        break;
      }
    }
  };

  /**
   * Called when a state is exited. This is only called if the state type changes
   */
  private onCurrentStateChange(
    oldState: CreatureState,
    newState: CreatureState
  ) {
    switch (newState.type) {
      case CreatureStateType.Death: {
        this.movingForce.force.x = 0;
        this.movingForce.force.y = 0;
        this.movingForce.force.z = 0;
        break;
      }
    }
  }

  /**
   * Performs a normal attack and returns if its successful
   */
  attack(): boolean {
    switch (this.getCurrentState().type) {
      case CreatureStateType.Idle: {
        this.attackAction();
        return true;
      }
      case CreatureStateType.Walk: {
        this.attackAction();
        return true;
      }
      case CreatureStateType.Jump: {
        break;
      }
      case CreatureStateType.Death: {
        break;
      }
      case CreatureStateType.NormalAttack: {
        break;
      }
    }
    return false;
  }

  private attackAction() {
    const nextAttackConfig = this.getMasterPlayerState().nextNormalAttackConfig;
    this.movingForce.force.x = 0;
    this.movingForce.force.y = 0;
    this.movingForce.force.z = 0;
    const momentum = nextAttackConfig.momentum;
    if (momentum) {
      const { x, y } = quatToVector2D(this.getQuaternion());
      this.applyMomentum({
        delay: momentum.delay,
        duration: momentum.duration,
        distance: momentum.distance,
        direction: { x, y: 0, z: y },
      });
    }
    this.setCurrentState({
      type: CreatureStateType.NormalAttack,
      duration: nextAttackConfig.duration,
      isComplete: false,
      animationType: nextAttackConfig.animationType,
    });
    this.normalAttackTimer.start(nextAttackConfig.duration);
  }

  /**
   * Moves the player
   */
  move(direction: Vector2) {
    switch (this.getCurrentState().type) {
      case CreatureStateType.Idle: {
        this.moveAction(direction);
        this.setCurrentState({
          type: vector2IsZero(direction)
            ? CreatureStateType.Idle
            : CreatureStateType.Walk,
        });
        break;
      }
      case CreatureStateType.Walk: {
        this.moveAction(direction);
        this.setCurrentState({
          type: vector2IsZero(direction)
            ? CreatureStateType.Idle
            : CreatureStateType.Walk,
        });
        break;
      }
      case CreatureStateType.Jump: {
        this.moveAction(direction);
        break;
      }
      case CreatureStateType.Death: {
        break;
      }
      case CreatureStateType.NormalAttack: {
        // Cannot move now but wait till able to move
        this.bufferedMoveDirection = direction;
        break;
      }
    }
  }

  /**
   * Does the move action with no state change
   */
  private moveAction(direction: Vector2) {
    this.movingForce.force = {
      x: direction.x * this.getMasterPlayerState().speed,
      y: 0,
      z: direction.y * this.getMasterPlayerState().speed,
    };
    if (!vector2IsZero(direction)) {
      this.setQuaternion(vector2DToQuat(direction));
    }
    this.bufferedMoveDirection = null;
  }

  /**
   * Jumps
   */
  jump() {
    switch (this.getCurrentState().type) {
      case CreatureStateType.Idle: {
        this.jumpAction();
        this.setCurrentState({
          type: CreatureStateType.Jump,
        });
        break;
      }
      case CreatureStateType.Walk: {
        this.jumpAction();
        this.setCurrentState({
          type: CreatureStateType.Jump,
        });
        break;
      }
      case CreatureStateType.Jump: {
        // Cannot jump when already jumping
        break;
      }
      case CreatureStateType.Death: {
        break;
      }
      case CreatureStateType.NormalAttack: {
        break;
      }
    }
  }

  /**
   * Does the jump action with no state change
   */
  private jumpAction() {
    const masterState = this.getMasterPlayerState();
    this.addVelocity({
      x: 0,
      y: masterState.jumpVelocity,
      z: 0,
    });
  }
}
